import PropTypes from 'prop-types';
import { Bed, Path, Clock, Check, ShoppingBag, Camera } from '@phosphor-icons/react';
import { vars, Heading, Stack, Inline, Text } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { DangerousHtmlContent, Icon } from '@eti/components';
import {
  SELF_TRANSFER,
  OVERNIGHT_STAY,
  STOP,
  ONE_DAY_STOPOVER,
  ONE_NIGHT_STOPOVER,
  SHORT_STOPOVER,
  TWO_NIGHTS_STOPOVER,
} from '../../constants/eventSegmentTypes';
import { IconWrapper, boundMargins } from './ItineraryComponents';

const wrapperStyles = css`
  background-color: ${vars.colors.page.background.primary};
`;

const eventSegmentStyles = (isStopoverAvailable) => css`
  ${isStopoverAvailable ? `padding: 0 0 16px;` : `padding: 16px 0;`}
  display: flex;
  position: relative;

  &::before {
    border-left: 1px dashed ${vars.colors.inputs.border};
    ${boundMargins};
    top: 0;
  }

  &::after {
    border-left: 1px dashed ${vars.colors.inputs.border};
    bottom: 0;
    ${boundMargins};
  }
`;

const bulletListStyles = css`
  list-style: unset;
  padding-inline-start: 20px;
  width: 100%;

  & li {
    line-height: 1.5;
    padding: 3px 0;
  }
`;

const stackWrapperStyles = css`
  padding-inline: 20px 16px;
`;

const placeHolderStyles = css`
  flex: 0 0 89px;
`;

const eventInfoStyles = css`
  font-size: 0.875rem;
`;

const eventSegmentContentStyles = css`
  align-items: center;
  display: flex;
  flex: 1;
`;

const headingStyles = (isSelfTransfer, isStopoverAvailable) => css`
  ${isSelfTransfer && `color:#0f7012`};
  ${isSelfTransfer && `a:not([class]), a:hover:not([class]) { color:#0f7012;`};
  ${isStopoverAvailable && `color: #0057a7`}
`;

const getSegmentTypesIcon = (type) => {
  switch (type) {
    case SELF_TRANSFER:
      return <Icon color="#0F7012" id="self-transfer" size="18px" />;
    case SHORT_STOPOVER:
      return <ShoppingBag color="#0057a7" size={18} weight="fill" />;
    case STOP:
      return <Clock size={18} weight="fill" />;
    case ONE_DAY_STOPOVER:
      return <Camera color="#0057a7" size={18} weight="fill" />;
    case OVERNIGHT_STAY:
      return <Bed size={18} weight="fill" />;
    case ONE_NIGHT_STOPOVER:
    case TWO_NIGHTS_STOPOVER:
      return <Bed color="#0057a7" size={18} weight="fill" />;
    default:
      return <Path size={18} weight="regular" />;
  }
};

const ItineraryEventSegment = ({
  description,
  isDescriptionAvailable,
  eventSegmentTitles,
  isSelfTransferDescriptionAvailable,
  types = [],
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const selfTransferDescription = (
    <ul className={bulletListStyles} data-testid="bullet-list">
      {p('SelfTransfer.IconsAndText.Baggage.Enabled') && (
        <li>{t('SelfTransfer.Result.Baggage.Text')}</li>
      )}
      {p('SelfTransfer.IconsAndText.Transfer.Enabled') && (
        <li>{t('SelfTransfer.Result.Transfer.Text')}</li>
      )}
      {p('SelfTransfer.IconsAndText.Guarantee.Enabled') && (
        <li>
          <Text asHtml>{t('SelfTransfer.Result.Guarantee.Text')}</Text>
        </li>
      )}
    </ul>
  );

  const hasFastTrackTextKey = Boolean(t('TripDetails.Details.StopoverInRome.FastTrack'));
  const hasAutoCheckinTextKey = Boolean(t('TripDetails.Details.StopoverInRome.AutoCheckin'));
  const hasExclusiveDealsTextKey = Boolean(t('TripDetails.Details.StopoverInRome.ExclusiveDeals'));

  const StopoverDescription = (stopoverType) => {
    switch (stopoverType) {
      case SHORT_STOPOVER:
        return (
          <>
            <div className={eventInfoStyles}>
              {t('TripDetails.Details.StopoverInRome.ShortStopover.Description')}
            </div>

            {hasFastTrackTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="short-stopover-fast-track-check-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.FastTrack')}
                </div>
              </Inline>
            )}
            {hasAutoCheckinTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="short-stopover-auto-checkin-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.AutoCheckin')}
                </div>
              </Inline>
            )}
          </>
        );
      case ONE_DAY_STOPOVER:
        return (
          <>
            <div className={eventInfoStyles}>
              {t('TripDetails.Details.StopoverInRome.OneDayStopover.Description')}
            </div>
            {hasFastTrackTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-day-stopover-fast-track-check-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.FastTrack')}
                </div>
              </Inline>
            )}
            {hasAutoCheckinTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-day-stopover-auto-checkin-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.AutoCheckin')}
                </div>
              </Inline>
            )}
            {hasExclusiveDealsTextKey && (
              <Inline noWrap spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-day-stopover-exclusive-deals-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.ExclusiveDeals')}
                </div>
              </Inline>
            )}
          </>
        );

      case ONE_NIGHT_STOPOVER:
        return (
          <>
            <div className={eventInfoStyles}>
              {t('TripDetails.Details.StopoverInRome.OneNightStopover.Description')}
            </div>

            {hasFastTrackTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-night-stopover-fast-track-check-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.FastTrack')}
                </div>
              </Inline>
            )}
            {hasAutoCheckinTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-night-stopover-auto-checkin-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.AutoCheckin')}
                </div>
              </Inline>
            )}
            {hasExclusiveDealsTextKey && (
              <Inline noWrap spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="one-night-stopover-exclusive-deals-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.ExclusiveDeals')}
                </div>
              </Inline>
            )}
          </>
        );

      case TWO_NIGHTS_STOPOVER:
        return (
          <>
            <div className={eventInfoStyles}>
              {t('TripDetails.Details.StopoverInRome.TwoNightsStopover.Description')}
            </div>

            {hasFastTrackTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="two-nights-stopover-fast-track-check-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.FastTrack')}
                </div>
              </Inline>
            )}
            {hasAutoCheckinTextKey && (
              <Inline spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="two-nights-stopover-auto-checkin-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.AutoCheckin')}
                </div>
              </Inline>
            )}
            {hasExclusiveDealsTextKey && (
              <Inline noWrap spacing={4}>
                <Check
                  color="#0057a7"
                  data-testid="two-nights-stopover-exclusive-deals-icon"
                  size={18}
                  weight="bold"
                />
                <div className={eventInfoStyles}>
                  {t('TripDetails.Details.StopoverInRome.ExclusiveDeals')}
                </div>
              </Inline>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const isStopoverAvailable = types.some(
    (segment) =>
      segment.type === SHORT_STOPOVER ||
      segment.type === ONE_DAY_STOPOVER ||
      segment.type === ONE_NIGHT_STOPOVER ||
      segment.type === TWO_NIGHTS_STOPOVER,
  );

  const isStopOvernightSelfTransferType = types.some(
    (segment) =>
      segment.type === STOP || segment.type === OVERNIGHT_STAY || segment.type === SELF_TRANSFER,
  );

  return (
    <div className={wrapperStyles} data-testid="tripDetails-eventSegment">
      <div className={eventSegmentStyles(isStopoverAvailable)}>
        <div className={placeHolderStyles} />
        <div className={eventSegmentContentStyles} data-testid="tripDetails-segment-eventToggle">
          <IconWrapper>
            <Icon id="bound-mark" size="8px" />
          </IconWrapper>
          <div className={stackWrapperStyles}>
            <Stack>
              {isStopOvernightSelfTransferType && (
                <Inline alignY="top" noWrap spacing={8}>
                  {getSegmentTypesIcon(STOP)}
                  <Heading
                    className={headingStyles(false)}
                    data-testid="tripDetails-segment-eventHeader-STOP"
                    level={4}
                  >
                    <DangerousHtmlContent content={eventSegmentTitles[STOP]} />
                  </Heading>
                </Inline>
              )}
              {!isStopoverAvailable
                ? types
                    .filter((filteredType) => filteredType.type !== STOP)
                    .map(({ type, legendNumber }) => (
                      <Stack key={`eventSegmentType-${legendNumber}-${type}`} spacing={8}>
                        <Inline alignY="top" noWrap spacing={8}>
                          {getSegmentTypesIcon(type)}
                          <Heading
                            className={headingStyles(type === SELF_TRANSFER)}
                            data-testid={`tripDetails-segment-eventHeader-${type}`}
                            level={4}
                          >
                            <DangerousHtmlContent content={eventSegmentTitles[type]} />
                          </Heading>
                        </Inline>
                        {isDescriptionAvailable && (
                          <div
                            className={eventInfoStyles}
                            data-testid="tripDetails-segment-eventInfo"
                          >
                            {type === SELF_TRANSFER && isSelfTransferDescriptionAvailable
                              ? selfTransferDescription
                              : description[type]}
                          </div>
                        )}
                      </Stack>
                    ))
                : types.map(({ type, legendNumber }) => (
                    <Stack key={`eventSegmentType-${legendNumber}-${type}`} spacing={8}>
                      {(type === SHORT_STOPOVER ||
                        type === ONE_DAY_STOPOVER ||
                        type === ONE_NIGHT_STOPOVER ||
                        type === TWO_NIGHTS_STOPOVER) && (
                        <>
                          <Inline alignY="top" noWrap spacing={8}>
                            {getSegmentTypesIcon(type)}
                            <Heading
                              className={headingStyles(type === SELF_TRANSFER, isStopoverAvailable)}
                              data-testid={`tripDetails-segment-eventHeader-${type}`}
                              level={4}
                            >
                              <DangerousHtmlContent content={eventSegmentTitles[type]} />
                            </Heading>
                          </Inline>
                          {StopoverDescription(type)}
                        </>
                      )}
                      {type === SELF_TRANSFER && isDescriptionAvailable && (
                        <>
                          <Inline alignY="top" noWrap spacing={8}>
                            {getSegmentTypesIcon(type)}
                            <Heading
                              className={headingStyles(type === SELF_TRANSFER)}
                              data-testid={`tripDetails-segment-eventHeader-${type}`}
                              level={4}
                            >
                              <DangerousHtmlContent content={eventSegmentTitles[type]} />
                            </Heading>
                          </Inline>
                          <div
                            className={eventInfoStyles}
                            data-testid="tripDetails-segment-eventInfo"
                          >
                            {type === SELF_TRANSFER && isSelfTransferDescriptionAvailable
                              ? selfTransferDescription
                              : description[type]}
                          </div>
                        </>
                      )}
                    </Stack>
                  ))}
            </Stack>
          </div>
        </div>
      </div>
    </div>
  );
};

ItineraryEventSegment.propTypes = {
  description: PropTypes.object,
  eventSegmentTitles: PropTypes.object,
  isDescriptionAvailable: PropTypes.bool,
  isSelfTransferDescriptionAvailable: PropTypes.bool,
  types: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.any,
    }),
  ),
};

export default ItineraryEventSegment;
